import { useState, useEffect } from 'react';
import { notificationApi } from '../services/api';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
      
      // Check if already subscribed
      navigator.serviceWorker.ready.then(async (registration) => {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
              setIsSubscribed(true);
          }
      });
    }
  }, []);

  const subscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Push] Step 1: Checking support');
      if (!isSupported) throw new Error('Push notifications not supported');

      console.log('[Push] Step 2: Requesting permission');
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      console.log('[Push] Permission result:', permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Permission denied');
      }

      console.log('[Push] Step 3: Waiting for service worker');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready');

      // Check for existing subscription and unsubscribe first
      console.log('[Push] Step 3.5: Checking for existing subscription');
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[Push] Found existing subscription, unsubscribing first');
        await existingSubscription.unsubscribe();
        console.log('[Push] Existing subscription removed');
      } else {
        console.log('[Push] No existing subscription found');
      }

      console.log('[Push] Step 4: Fetching VAPID public key from backend');
      const { data } = await notificationApi.getPublicKey();
      console.log('[Push] Received data:', data);
      if (!data) throw new Error('Failed to get public key');
      const convertedVapidKey = urlBase64ToUint8Array(data.key);
      console.log('[Push] VAPID key converted');

      console.log('[Push] Step 5: Subscribing to push manager');
      // Add timeout to prevent infinite hanging
      const subscribePromise = registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      }).catch((err) => {
        console.error('[Push] pushManager.subscribe() error:', err);
        throw err;
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Push subscription timed out after 10 seconds')), 10000)
      );
      const subscription = await Promise.race([subscribePromise, timeoutPromise]) as PushSubscription;
      console.log('[Push] Push subscription created:', subscription.endpoint);

      console.log('[Push] Step 6: Sending subscription to backend');
      await notificationApi.subscribe(subscription);
      console.log('[Push] Subscription sent to backend successfully');

      setIsSubscribed(true);
      console.log('[Push] ✅ All steps completed');
    } catch (err: any) {
      console.error('[Push] ❌ Failed to subscribe:', err);
      setError(err.message || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
      // Logic to unsubscribe locally (and optionally tell backend to delete)
      // For now, simpler to just "disable" by unregistering or similar, 
      // but standard web push usually just involves letting the sub expire or manually removing from browser settings.
      // We can implement explicit unsubscribe later if needed.
      alert("To unsubscribe, please reset permissions in your browser settings.");
  }

  const sendTestNotification = async () => {
      try {
          await notificationApi.test();
      } catch (err) {
          console.error("Test failed", err);
      }
  }

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}
